/**
 * 67.75 steps 1d–1f: the motor pool's automated crate handlers — what moves the frames (rendering/vehicleFrame.ts) round a base,
 * stacks them and sets them down to unpack. The owner picked, on 1e: *"Ok corner bots and stacker crane are my picks"* (the
 * plate, the mast crane, 1d's gantry, straddle carrier and loader are in the history: 1d 92d9e7cb, 1e 5651127c). No cab on
 * either: sensor pucks, amber beacons, caution bands, AUTO, the company's livery.
 *   - `corner` (the mover, and the unpack): four small jack-and-wheel robots, one at each of the frame's bottom corner castings
 *     (a pin into the casting's end face, on a carriage up a column). They arrive, clamp, lift together, crab or drive, set
 *     down (`spread`, `steer`). No legs on the frame, no paving: they lift a frame off the ground anywhere.
 *   - `arm` (stacking): a stacker arm, a knuckle boom on a compact wheeled base with outriggers, taking a frame by its four top
 *     castings with a `spreader` (twist-lock blocks at its corners) and carrying it crosswise at its head: three high at 6.5 m
 *     reach.
 * `pose.lift` is, for the arm, the spreader's underside over the ground (a carried frame's top); for the corner robots, the
 * carried frame's bottom. The builder returns where the carried frame's floor goes and its turn. Rebuilt per pose here (props,
 * ~1.7–1.9k triangles); one draw each (the corner robots' four in one), Lambert like the frames. Presentation only. The sheet is
 * design_sheets/motor_pool.html.
 */
import * as THREE from 'three';
import { Spec, mergedTier, FRAME, type Livery } from './orbitShips';
import type { UnitPalette } from './unitPalette';

export type HandlerKind = 'corner' | 'arm';
export const HANDLER_KINDS: readonly HandlerKind[] = ['corner', 'arm'];
export interface HandlerPose {
  /** the arm: the spreader's underside above the ground (the carried frame's top); the corner robots: the carried frame's bottom; metres */
  lift: number;
  /** the stacker arm's head out from its pedestal (metres) */
  reach?: number;
  /** the corner robots: 0 docked at their corners, 1 arriving (~3 m out) */
  spread?: number;
  /** the corner robots' wheels: 0 along the frame, 1 turned across (crabbing sideways) */
  steer?: number;
}
/** The stacker arm's two booms end to end (metres). */
export const HANDLER = { armBooms: 14.4 };

/** The spreader: a frame-top's beam with twist-lock blocks at its corners, its underside at `y`; `cross` turns it across x. */
function spreader(s: Spec, x: number, y: number, z: number, cross = false): void {
  const L = FRAME.length - 0.2, W = FRAME.width - 0.4, sw = (a: number, b: number): [number, number] => cross ? [b, a] : [a, b];
  for (const sx of [-1, 1]) { const [px, pz] = sw(sx * W / 2, 0), [w, d] = sw(0.3, L); s.box(w, 0.45, d, x + px, y + 0.45, z + pz, 'caution'); }
  for (const sz of [-1, 1]) { const [px, pz] = sw(0, sz * (L / 2 - 0.15)), [w, d] = sw(W, 0.3); s.box(w, 0.45, d, x + px, y + 0.45, z + pz, 'caution'); }
  { const [w, d] = sw(1.4, 1.2); s.box(w, 0.6, d, x, y + 0.85, z, 'machDk'); }
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const [px, pz] = sw(sx * (FRAME.width / 2 - 0.16), sz * (FRAME.length / 2 - 0.16));
    s.box(0.3, 0.3, 0.3, x + px, y + 0.12, z + pz, 'machDk');
  }
}
/** The autonomous kit: a sensor puck (lidar) and a beacon. */
function puck(s: Spec, x: number, y: number, z: number, r = 0.24): void {
  s.cyl(r, r * 1.25, 'y', x, y + r * 0.62, z, 'machDk', { seg: 10 });
  s.cyl(r * 0.83, r / 2, 'y', x, y + r * 1.5, z, 'cream', { seg: 10 });
}
const beacon = (s: Spec, x: number, y: number, z: number, r = 0.22) => s.sphere(r, x, y + r, z, 'caution');
/** An outrigger: a beam from the chassis at `a` out to a jack leg and its pad at (x, z) on the ground. */
function outrigger(s: Spec, a: THREE.Vector3, x: number, z: number, h: number): void {
  s.rod(a, new THREE.Vector3(x, h, z), 0.16, 'teamDk', { seg: 4 });
  s.box(0.26, h, 0.26, x, h / 2, z, 'caution');
  s.box(0.8, 0.08, 0.8, x, 0.04, z, 'machDk');
}

// ---- the mover -----------------------------------------------------------------------------------------------------------------

/** The four corner robots round a frame centred on the origin, its bottom at `lift`. */
function corner(s: Spec, o: HandlerPose): THREE.Vector3 {
  const sp = o.spread ?? 0, st = (o.steer ?? 0) * Math.PI / 2, Lh = FRAME.length / 2, cy = 0.16 + o.lift;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const x = sx * (FRAME.width / 2 - 0.16 + sp * 1.6), z0 = sz * sp * 3;
    // the chassis out past the frame's end, its battery hump, four steered wheels
    const cz = z0 + sz * (Lh + 0.75);
    s.box(0.8, 0.4, 1.1, x, 0.42, cz, 'team');
    s.box(0.82, 0.08, 1.12, x, 0.6, cz, 'teamLt');
    s.box(0.6, 0.3, 0.5, x, 0.79, z0 + sz * (Lh + 1.0), 'machDk');
    for (const wx of [-1, 1]) for (const wz of [-1, 1]) s.cyl(0.2, 0.14, 'x', x + wx * 0.45, 0.2, cz + wz * 0.36, 'rubber', { seg: 8, ry: st });
    // the column against the frame's end, its carriage and the pin into the bottom casting's end face
    const colZ = z0 + sz * (Lh + 0.42);
    s.box(0.34, 2.0, 0.34, x, 1.2, colZ, 'team');
    for (let k = 0; k < 2; k++) s.box(0.36, 0.22, 0.36, x, 1.25 + k * 0.5, colZ, 'caution');
    s.box(0.44, 0.52, 0.24, x, cy, z0 + sz * (Lh + 0.13), 'machLt');
    s.box(0.1, 0.1, 0.14, x, cy, z0 + sz * (Lh - 0.05), 'metal');
    puck(s, x, 2.2, colZ, 0.13);
    beacon(s, x, 0.94, z0 + sz * (Lh + 1.0), 0.09);
  }
  return new THREE.Vector3(0, o.lift, 0);
}

// ---- the crane -----------------------------------------------------------------------------------------------------------------

/** The stacker arm: its base along z, facing +z; the frame carried crosswise at its head, `reach` out from its pedestal. */
function arm(s: Spec, o: HandlerPose): THREE.Vector3 {
  const a = HANDLER.armBooms / 2, b = a, P = new THREE.Vector3(0, 2.6, -1.0);
  let E = new THREE.Vector3(0, o.lift + 1.6, P.z + (o.reach ?? 6.5));
  const d0 = E.distanceTo(P);
  if (d0 > a + b - 0.05) E = P.clone().addScaledVector(E.clone().sub(P).normalize(), a + b - 0.05);
  const d = E.distanceTo(P), phi = Math.atan2(E.y - P.y, E.z - P.z), al = Math.acos((a * a + d * d - b * b) / (2 * a * d));
  const Kn = P.clone().add(new THREE.Vector3(0, Math.sin(phi + al) * a, Math.cos(phi + al) * a));
  // the base, its wheels, the counterweight, the outriggers
  s.box(2.6, 1.0, 5.4, 0, 0.95, -0.4, 'team');
  s.box(2.62, 0.2, 5.42, 0, 1.3, -0.4, 'teamLt');
  s.box(2.4, 1.2, 1.0, 0, 2.0, -2.8, 'slate');
  for (const sx of [-1, 1]) for (const z of [-2.4, -0.8, 1.6]) s.cyl(0.42, 0.4, 'x', sx * 1.3, 0.42, z, 'rubber', { seg: 10 });
  for (const sx of [-1, 1]) for (const [z, oz] of [[-2.6, -3.2], [1.8, 2.2]]) outrigger(s, new THREE.Vector3(sx * 1.2, 1.1, z), sx * 3.3, oz, 1.1);
  s.name(1.31, 0.95, -1.6, 0.45, 2.6);
  s.text('AUTO', 1.31, 0.95, 1.3, 0.4, 1.4, 'caution');
  // the pedestal, the main boom and its ram, the knuckle, the telescoping outer boom and its ram, the rotator, the spreader
  s.cyl(0.8, 1.2, 'y', 0, 2.0, P.z, 'machDk', { seg: 12 });
  s.rod(P, Kn, 0.38, 'team', { seg: 6 });
  s.rod(new THREE.Vector3(0, 1.9, 0.3), P.clone().lerp(Kn, 0.45), 0.16, 'machLt', { seg: 6 });
  s.box(0.9, 0.9, 0.9, Kn.x, Kn.y, Kn.z, 'machDk');
  const mid = Kn.clone().lerp(E, 0.55);
  s.rod(Kn, mid, 0.32, 'team', { seg: 6 });
  s.rod(mid, E, 0.24, 'machLt', { seg: 6 });
  s.rod(P.clone().lerp(Kn, 0.75), Kn.clone().lerp(E, 0.3), 0.12, 'machLt', { seg: 6 });
  s.cyl(0.3, 0.4, 'y', E.x, E.y - 0.2, E.z, 'machDk', { seg: 10 });
  s.rod(new THREE.Vector3(E.x, E.y - 0.4, E.z), new THREE.Vector3(E.x, o.lift + 1.15, E.z), 0.1, 'metal', { seg: 4 });
  spreader(s, 0, o.lift, E.z, true);
  puck(s, 0, 1.4, 2.0, 0.18); beacon(s, -1.0, 2.6, -2.8); beacon(s, 1.0, 2.6, -2.8);
  return new THREE.Vector3(0, o.lift - FRAME.height, E.z);
}

const BUILD: Record<HandlerKind, (s: Spec, o: HandlerPose) => THREE.Vector3> = { corner, arm };
/**
 * Build a handler in a pose: its mesh (one draw), its triangles, and where a frame it carries goes (its floor centre; `ry` its
 * turn: the arm carries crosswise).
 */
export function buildHandler(kind: HandlerKind, p: UnitPalette, livery: Livery, pose: HandlerPose): { mesh: THREE.Mesh; triangles: number; carry: THREE.Vector3; ry: number } {
  const spec = new Spec(livery);
  const carry = BUILD[kind](spec, pose);
  const g = mergedTier(spec, p, false);
  g.setAttribute('color', g.getAttribute('paint'));
  g.deleteAttribute('glow');
  return { mesh: new THREE.Mesh(g, new THREE.MeshLambertMaterial({ vertexColors: true })), triangles: g.attributes.position.count / 3, carry, ry: kind === 'arm' ? Math.PI / 2 : 0 };
}
